import React from 'react';
import { ShieldCheck, FileCheck2, Award, BadgeCheck, Sparkles } from 'lucide-react';
import { Reveal } from '../../hooks/useReveal';

/**
 * Certifications & Licenses — premium glassmorphism trust strip.
 *
 * Three cards (GST Certificate, Pest Control License, MSME Registration)
 * with frosted glass, animated shine sweep, glow border on hover and a
 * pulsing "Verified" tick. Sits just after the TreatmentShowcase strip
 * to compound trust right after the visual proof.
 */
const CERTIFICATIONS = [
  {
    key: 'gst',
    icon: FileCheck2,
    badge: 'Govt. of India',
    title: 'GST Certificate',
    code: 'GSTIN · 05LOWXXXXXXX1ZP',
    blurb:
      'Registered taxpayer issuing GST-compliant invoices for every service — transparent billing, zero surprises.',
    glow: 'from-emerald-400/40 via-emerald-300/10 to-transparent',
    ring: 'group-hover:shadow-[0_0_45px_-8px_rgba(16,185,129,0.55)]',
    chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30',
  },
  {
    key: 'license',
    icon: ShieldCheck,
    badge: 'Agri. Department',
    title: 'Pest Control License',
    code: 'License · UK-AGRI/7XXXXXXXX28',
    blurb:
      'Approved by the Agriculture Department under the Insecticides Act, 1968 to handle and apply pest control chemicals — safely and lawfully.',
    glow: 'from-sky-400/40 via-sky-300/10 to-transparent',
    ring: 'group-hover:shadow-[0_0_45px_-8px_rgba(56,189,248,0.55)]',
    chip: 'bg-sky-500/15 text-sky-300 border-sky-400/30',
  },
  {
    key: 'msme',
    icon: Award,
    badge: 'Ministry of MSME',
    title: 'MSME Registration',
    code: 'Udyam · UDYAM-UXXXXXXXXXX68',
    blurb:
      'Recognised Micro, Small & Medium Enterprise — committed to quality, jobs and trusted local service.',
    glow: 'from-amber-400/40 via-amber-300/10 to-transparent',
    ring: 'group-hover:shadow-[0_0_45px_-8px_rgba(251,191,36,0.55)]',
    chip: 'bg-amber-500/15 text-amber-300 border-amber-400/30',
  },
];

export default function Certifications() {
  return (
    <section
      className="relative py-16 sm:py-20 overflow-hidden bg-[#06121F]"
      data-testid="certifications-section"
      style={{
        backgroundImage:
          'radial-gradient(circle at 15% 20%, rgba(16,185,129,0.18) 0%, transparent 45%), ' +
          'radial-gradient(circle at 85% 80%, rgba(56,189,248,0.18) 0%, transparent 45%), ' +
          'linear-gradient(180deg, #06121F 0%, #0A1A2E 60%, #06121F 100%)',
      }}
    >
      {/* Subtle grid texture */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), ' +
            'linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Reveal>
          <div className="text-center mb-12 sm:mb-16">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 backdrop-blur-md border border-white/10 text-[11px] uppercase tracking-[0.25em] font-bold text-emerald-300 mb-4"
              data-testid="certifications-eyebrow"
            >
              <BadgeCheck size={13} /> Verified & Licensed
            </div>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3"
              style={{ fontFamily: '"Playfair Display", "Cormorant Garamond", serif' }}
            >
              Built on <span className="italic text-emerald-300">Trust</span>, Backed by Paperwork
            </h2>
            <p className="text-sm sm:text-base text-slate-300/80 max-w-2xl mx-auto leading-relaxed">
              Every spray, every signature, every invoice is backed by real Government certifications.
              We are fully licensed, GST-compliant and MSME-registered — so you can welcome us into
              your home with complete peace of mind.
            </p>
          </div>
        </Reveal>

        {/* Glass cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {CERTIFICATIONS.map((c, i) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.key} delay={i * 0.12}>
                <div
                  data-testid={`certification-card-${c.key}`}
                  className={`group relative h-full rounded-2xl p-[1px] overflow-hidden transition-all duration-500 ${c.ring}`}
                >
                  {/* Animated glow border — soft gradient that brightens on hover */}
                  <div
                    className="absolute inset-0 rounded-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background:
                        'linear-gradient(120deg, rgba(16,185,129,0.55), rgba(56,189,248,0.55), rgba(251,191,36,0.55), rgba(16,185,129,0.55))',
                      backgroundSize: '300% 100%',
                      animation: 'certBorderShift 6s ease-in-out infinite',
                    }}
                    aria-hidden
                  />

                  {/* Inner glass card */}
                  <div className="relative h-full rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/10 p-6 sm:p-7 overflow-hidden">
                    {/* Radial glow */}
                    <div
                      className={`absolute -top-20 -right-16 w-56 h-56 rounded-full blur-3xl pointer-events-none bg-gradient-to-br ${c.glow}`}
                      aria-hidden
                    />

                    {/* Shine sweep */}
                    <div
                      className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{
                        background:
                          'linear-gradient(110deg, transparent 35%, rgba(255,255,255,0.18) 50%, transparent 65%)',
                        animation: 'certShine 2.4s ease-in-out infinite',
                      }}
                      aria-hidden
                    />

                    {/* Top row: icon + verified pill */}
                    <div className="relative flex items-start justify-between mb-5">
                      <div className="relative w-14 h-14 rounded-xl bg-gradient-to-br from-white/[0.12] to-white/[0.02] border border-white/15 inline-flex items-center justify-center text-white shadow-inner">
                        <Icon size={26} strokeWidth={1.8} />
                        {/* tiny sparkle */}
                        <Sparkles
                          size={12}
                          className="absolute -top-1.5 -right-1.5 text-emerald-300 animate-pulse"
                          aria-hidden
                        />
                      </div>
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 rounded-full border ${c.chip}`}
                      >
                        <BadgeCheck size={11} /> {c.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <h3
                      className="relative text-xl sm:text-2xl font-bold text-white mb-2 leading-tight"
                      style={{ fontFamily: '"Playfair Display", serif' }}
                    >
                      {c.title}
                    </h3>

                    {/* Code */}
                    <div className="relative text-[11px] uppercase tracking-[0.18em] font-mono text-emerald-300/90 mb-4">
                      {c.code}
                    </div>

                    {/* Blurb */}
                    <p className="relative text-sm text-slate-300/85 leading-relaxed">
                      {c.blurb}
                    </p>

                    {/* Footer mark */}
                    <div className="relative mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-white/70">
                        <span className="relative inline-flex w-2 h-2 rounded-full bg-emerald-400">
                          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                        </span>
                        Active & Verified
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">
                        EntoRid
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Trust footnote */}
        <Reveal delay={0.4}>
          <p
            className="mt-10 sm:mt-12 text-center text-[12px] sm:text-sm text-slate-400/80 max-w-3xl mx-auto"
            data-testid="certifications-footnote"
          >
            <span className="text-emerald-300 font-semibold">Documents available on request.</span>{' '}
            We are happy to share scanned copies of any of our certifications before the technician
            arrives at your doorstep — because trust starts before the first knock.
          </p>
        </Reveal>
      </div>

      {/* Local keyframes (scoped via global stylesheet name; no extra deps) */}
      <style>{`
        @keyframes certShine {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(120%); }
        }
        @keyframes certBorderShift {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }
      `}</style>
    </section>
  );
}
